import { lazy, Suspense } from 'react';

import { useCheckoutContext } from '@zooplus/checkout-state';

import { CheckoutSkeleton } from '@components/common/checkout-skeleton';
import useFetchRecommendations from '@hooks/use-fetch-recommendations';
import { CHECKOUT_STATE_PROJECT_ID, breakpointsNumeric } from '@utilities/constants';
import { isArrayNotEmpty } from '@utilities/utils';
import '@zooplus/recommendations-component/dist/es/shsvs-recommendations-component.min.css';

const RecommendationsComponent = lazy(() =>
  import('@zooplus/recommendations-component').then((module) => ({
    default: module.RecommendationsCarousel,
  })),
);

interface RecommendationsCarouselProps {
  pageType: string;
  cartProducts?: string;
  cartArticles?: string;
}

const RecommendationsCarousel = ({
  pageType,
  cartProducts,
  cartArticles,
}: RecommendationsCarouselProps) => {
  const {
    checkoutState,
    isLoading,
    setPrevArticle,
    setArticleQuantity,
    mainTogglesMap = {},
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const cart = checkoutState?.cart;
  const isNewPricingEnabled = mainTogglesMap.NEW_PRICING_COMPONENT_IN_CART_AND_PREVIEW;

  const { recommendationsList } = useFetchRecommendations(pageType, cartProducts, cartArticles, {
    sid: cart?.sid,
  });

  const breakpoints = {
    '(min-width: 440px)': { slidesToScroll: 3 },
    '(min-width: 600px)': { slidesToScroll: 4 },
    [`(min-width: ${breakpointsNumeric.breakpointMd}px)`]: { slidesToScroll: 5 },
    [`(min-width: ${breakpointsNumeric.breakpointLg}px)`]: {
      slidesToScroll: 5,
    },
    '(min-width: 1290px)': {
      slidesToScroll: 6,
    },
  };

  return (
    <>
      {isArrayNotEmpty(recommendationsList) &&
        recommendationsList.map((recommendation, index) => (
          <Suspense key={index.toString()} fallback={<CheckoutSkeleton isFullPage={false} />}>
            <RecommendationsComponent
              caption={recommendation.caption}
              recommendations={recommendation.recommendations}
              priceVersion={isNewPricingEnabled ? '2024' : 'old'}
              emblaOptions={{
                loop: true,
                breakpoints,
                align: 'center',
                slidesToScroll: 2,
              }}
              addToCartButton={{
                loadingIndicator: !!isLoading,
                setRestoreArticle: setPrevArticle!,
                onAddToCartClick: setArticleQuantity!,
              }}
            />
          </Suspense>
        ))}
    </>
  );
};

export default RecommendationsCarousel;
